import React, { useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

/**
 * Toast Notification Component
 * Monochromatic turquoise design matching Plugly AI brand
 */

let toastId = 0;
const toastListeners = new Set();

export const toast = {
  success: (message, options = {}) => showToast('success', message, options),
  error: (message, options = {}) => showToast('error', message, options),
  warning: (message, options = {}) => showToast('warning', message, options),
  info: (message, options = {}) => showToast('info', message, options),
};

function showToast(type, message, options) {
  const id = ++toastId;
  const toast = { id, type, message, ...options };
  toastListeners.forEach(listener => listener(toast));
  return id;
}

export function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const listener = (toast) => {
      setToasts(prev => [...prev, toast]);
      
      // Auto-dismiss after duration
      const duration = toast.duration || 5000;
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toast.id));
      }, duration);
    };

    toastListeners.add(listener);
    return () => toastListeners.delete(listener);
  }, []);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map(toast => (
        <Toast key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
}

function Toast({ id, type, message, title, onClose }) {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(onClose, 300);
  };

  const config = {
    success: {
      icon: CheckCircle,
      bgClass: 'bg-gradient-to-r from-teal-500/90 to-teal-600/90',
      borderClass: 'border-teal-400/50',
      iconClass: 'text-teal-100',
      textClass: 'text-white',
    },
    error: {
      icon: AlertCircle,
      bgClass: 'bg-gradient-to-r from-red-500/90 to-red-600/90',
      borderClass: 'border-red-400/50',
      iconClass: 'text-red-100',
      textClass: 'text-white',
    },
    warning: {
      icon: AlertTriangle,
      bgClass: 'bg-gradient-to-r from-teal-500/90 to-cyan-600/90',
      borderClass: 'border-teal-400/50',
      iconClass: 'text-teal-100',
      textClass: 'text-white',
    },
    info: {
      icon: Info,
      bgClass: 'bg-gradient-to-r from-teal-500/90 to-cyan-500/90',
      borderClass: 'border-teal-400/50',
      iconClass: 'text-teal-100',
      textClass: 'text-white',
    },
  };

  const { icon: Icon, bgClass, borderClass, iconClass, textClass } = config[type] || config.info;

  return (
    <div
      className={`
        pointer-events-auto
        flex items-start gap-3 p-4 rounded-lg border backdrop-blur-md
        shadow-lg shadow-black/20
        min-w-[320px] max-w-[420px]
        ${bgClass} ${borderClass}
        transition-all duration-300 ease-out
        ${isExiting ? 'opacity-0 translate-x-8 scale-95' : 'opacity-100 translate-x-0 scale-100'}
        animate-slide-in-right
      `}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 ${iconClass}`}>
        <Icon className="w-5 h-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <div className={`font-semibold mb-1 ${textClass}`}>
            {title}
          </div>
        )}
        <div className={`text-sm ${textClass} opacity-95`}>
          {message}
        </div>
      </div>

      {/* Close Button */}
      <button
        onClick={handleClose}
        className={`
          flex-shrink-0 ${iconClass} hover:opacity-70
          transition-opacity duration-200
          focus:outline-none focus:ring-2 focus:ring-white/30 rounded
        `}
        aria-label="Close notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// Add animation styles
const style = document.createElement('style');
style.textContent = `
  @keyframes slide-in-right {
    from {
      opacity: 0;
      transform: translateX(2rem) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
  }
  
  .animate-slide-in-right {
    animation: slide-in-right 0.3s ease-out;
  }
`;
document.head.appendChild(style);

export default Toast;
